import { useAllUsers } from "@api";

/**
 * Resolves a user guid to a display name on the client, so endpoints can return the guid rather
 * than each one re-fetching the merchant's user list to spell out a name.
 *
 * Answers only from the cached list, which is scoped to the caller's merchant. There is
 * deliberately no per-id fallback: the only by-id lookup available is not merchant scoped, and
 * resolving a name is not worth reaching outside the merchant to do it.
 *
 * The list covers everyone with an active membership, Ontik support users included — they are
 * served flagged and filtered out only where people are being listed. Someone whose membership
 * has since been deactivated is not in it and resolves to null, which is what the server did
 * when it resolved these names itself. Keeping the list fresh is a cache-invalidation concern:
 * see invalidateUsers.
 *
 * Returns null while the list loads, and for anyone it does not cover.
 */
export default function useUserName(userGuid) {
  const { data: users = [] } = useAllUsers();

  if (!userGuid) {
    return null;
  }

  return users.find((u) => u.id === userGuid)?.name ?? null;
}
